/**
 * Statistics API Service
 *
 * Provides access to custom statistics calculations with flexible date ranges.
 */

import { post, get, del, patch } from "../client";
import type {
  StatisticsRequest,
  StatisticsResponse,
  StatisticField,
  PeriodReturnRequest,
  PeriodReturnResponse,
  ResourceBenchmarksRequest,
  ResourceBenchmarksResponse,
  StatsView,
  StatsViewListResponse,
  StatsViewCreate,
  StatsViewUpdate,
  StatisticsTableRequest,
  StatisticsTableResponse,
} from "./types";

export * from "./types";

/**
 * Calculate custom statistics for resources with specified parameters.
 *
 * @param request - The statistics request containing resources, fields, and date range
 * @param benchmarkId - Optional benchmark ID for relative statistics calculations
 * @returns Promise resolving to statistics response
 */
export async function calculateCustomStatistics(
  request: StatisticsRequest,
  benchmarkId?: string
): Promise<StatisticsResponse> {
  const params = benchmarkId ? `?benchmark_id=${encodeURIComponent(benchmarkId)}` : "";
  return post<StatisticsResponse>(`/statistics/custom${params}`, request);
}

/**
 * Get list of available statistics that can be calculated.
 *
 * @returns Promise resolving to list of available statistic field names
 */
export async function getAvailableStatistics(): Promise<{
  statistics: StatisticField[];
}> {
  return get<{ statistics: StatisticField[] }>("/statistics/available");
}

/**
 * Calculate period returns for resources.
 * 
 * This uses the backend to calculate returns for various time periods
 * (YTD, MTD, QTD, calendar years, quarters, months) in a single API call.
 *
 * @param request - The period return request containing resources and periods
 * @returns Promise resolving to period returns for each resource
 */
export async function calculatePeriodReturns(
  request: PeriodReturnRequest
): Promise<PeriodReturnResponse> {
  return post<PeriodReturnResponse>("/statistics/period-returns", request);
}

/**
 * Get list of available periods for period return calculations.
 *
 * @param asOfDate - Optional as-of date (defaults to today on backend)
 * @returns Promise resolving to list of available periods grouped by type
 */
export async function getAvailablePeriods(asOfDate?: string): Promise<{
  as_of_date: string;
  periods: Array<{ id: string; name: string; group: string }>;
}> {
  const params = asOfDate ? `?as_of_date=${asOfDate}` : "";
  return get<{
    as_of_date: string;
    periods: Array<{ id: string; name: string; group: string }>;
  }>(`/statistics/available-periods${params}`);
}

/**
 * Look up primary benchmarks for resources from their snapshots.
 * 
 * This is used to determine which resources have benchmarks configured,
 * enabling relative statistics calculations (beta, correlation, etc.).
 *
 * @param request - Resources to look up benchmarks for
 * @returns Promise resolving to benchmark info for each resource
 */
export async function getResourceBenchmarks(
  request: ResourceBenchmarksRequest
): Promise<ResourceBenchmarksResponse> {
  return post<ResourceBenchmarksResponse>("/statistics/resource-benchmarks", request);
}

// ============================================================================
// Stats Views API - Save/Load Column Configurations
// ============================================================================

/**
 * List all saved stats views for the current company.
 * 
 * @returns Promise resolving to list of stats views
 */
export async function listStatsViews(): Promise<StatsViewListResponse> {
  return get<StatsViewListResponse>("/statistics/views");
}

/**
 * Get a specific stats view by ID.
 * 
 * @param viewId - The ID of the view to retrieve
 * @returns Promise resolving to the stats view
 */
export async function getStatsView(viewId: string): Promise<StatsView> {
  return get<StatsView>(`/statistics/views/${viewId}`);
}

/**
 * Create a new stats view.
 * 
 * @param request - The view data to create
 * @returns Promise resolving to the created view
 */
export async function createStatsView(request: StatsViewCreate): Promise<StatsView> {
  return post<StatsView>("/statistics/views", request);
}

/**
 * Update an existing stats view.
 * 
 * @param viewId - The ID of the view to update
 * @param request - The fields to update
 * @returns Promise resolving to the updated view
 */
export async function updateStatsView(
  viewId: string,
  request: StatsViewUpdate
): Promise<StatsView> {
  return patch<StatsView>(`/statistics/views/${viewId}`, request);
}

/**
 * Delete a stats view.
 * 
 * @param viewId - The ID of the view to delete
 */
export async function deleteStatsView(viewId: string): Promise<void> {
  return del(`/statistics/views/${viewId}`);
}

// ============================================================================
// Unified Table API - Single endpoint for all statistics and period returns
// ============================================================================

/**
 * Calculate all statistics and period returns for a table view in a single call.
 * 
 * This is the unified endpoint that accepts a columns array and returns all data
 * in the exact same order. Use this instead of separate calls to calculateCustomStatistics
 * and calculatePeriodReturns.
 *
 * @param request - The table request containing resources, columns, and date range
 * @returns Promise resolving to table response with values in column order
 */
export async function calculateTable(
  request: StatisticsTableRequest
): Promise<StatisticsTableResponse> {
  return post<StatisticsTableResponse>("/statistics/table", request);
}

